import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as SummaryIcon } from "../../../assets/summary.icon.svg";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { CrawlRun } from "../../../common/interfaces/admin/crawl.interface";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import {
    downloadCrawlItemsCsv,
    fetchCrawlRuns
} from "../../../api/admin/content-providers/endpoints-referrers";
import { ModalTemplate } from "../../modal-template/modal-template.comp";
import { crawlCountsSummary } from "../../admin/crawl-banner/crawl-banner.comp";
import CrawlSettingsDialog from "../crawl-settings/crawl-settings-dialog.comp";
import {
    BOUNDARY_LABELS,
    REVIEW_POLICY_LABELS,
    SCHEDULE_LABELS
} from "../../crawl-settings/crawl-settings.util";

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="provider-properties-row">
        <span className="provider-properties-row__label">{label}</span>
        <span className="provider-properties-row__value">{children}</span>
    </div>
);

const formatDate = (value?: string | null): string => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const runTriggerSuffix = (run: CrawlRun): string =>
    run.trigger === "scheduled" ? " (scheduled)" : run.trigger === "page" ? " (single page)" : "";

const runStatusLabel = (run: CrawlRun): string =>
    `${run.status.replace(/_/g, " ")} — ${crawlCountsSummary(run.counts)}`;

/** Web site (webScraper) section: crawl summary, schedule, review policy, last run. */
const WebSiteProperties = ({
    provider,
    handleClose
}: {
    provider: Provider;
    handleClose: () => void;
}) => {
    const navigate = useNavigate();
    const { user } = FirebaseAuthContainer.useContainer();
    const { loadProviders } = ProvidersContainer.useContainer();
    const [runs, setRuns] = useState<CrawlRun[]>([]);
    const [editOpen, setEditOpen] = useState(false);
    const empty = "—";
    const config = provider.crawlConfig;

    useEffect(() => {
        if (!user) return;
        fetchCrawlRuns(user.companyId, provider.id)
            .then(response => setRuns(response.data))
            .catch(() => setRuns([]));
    }, [user, provider.id]);

    const pending = runs.find(run => run.status === "awaiting_review");

    // Run report CSV (plan M6): the whole inventory of one crawl, for Excel.
    const downloadCsv = (run: CrawlRun) => {
        if (!user) return;
        downloadCrawlItemsCsv(user.companyId, provider.id, run.id)
            .then(response => {
                const url = URL.createObjectURL(response.data);
                const link = document.createElement("a");
                link.href = url;
                link.download = `crawl-${run.id.slice(0, 8)}.csv`;
                link.click();
                URL.revokeObjectURL(url);
            })
            .catch(() => undefined);
    };
    const limits = config
        ? `${config.maxPages} pages, ${config.maxDocuments} documents, depth ${config.maxDepth}${
              config.maxRunMs ? `, ${Math.round(config.maxRunMs / 60000)} minutes` : ""
          }`
        : empty;
    const boundary = config
        ? config.boundary.mode === "prefixes"
            ? `${BOUNDARY_LABELS.prefixes}: ${(config.boundary.prefixes ?? []).join(", ")}`
            : BOUNDARY_LABELS[config.boundary.mode]
        : empty;

    return (
        <>
            <Row label="Start URLs">
                {config?.seedUrls?.length ? config.seedUrls.join(", ") : empty}
            </Row>
            <Row label="Stay within">{boundary}</Row>
            <Row label="Limits">{limits}</Row>
            <Row label="Includes">
                {config
                    ? [
                          "pages",
                          config.includeDocuments ? "linked documents" : null,
                          config.includeMedia ? "audio and video" : null
                      ]
                          .filter(Boolean)
                          .join(", ")
                    : empty}
            </Row>
            <Row label="Schedule">{SCHEDULE_LABELS[provider.crawlSchedule ?? "off"]}</Row>
            {provider.crawlSchedule && provider.crawlSchedule !== "off" && (
                <Row label="Next crawl">
                    {formatDate(provider.nextCrawlAt) || "at the next check"}
                </Row>
            )}
            <Row label="Review policy">
                {REVIEW_POLICY_LABELS[provider.reviewPolicy ?? "always"]}
            </Row>
            <Row label="Crawl result emails">
                {config?.notifyEmails?.length ? config.notifyEmails.join(", ") : "None"}
            </Row>
            <Row label="robots.txt">
                {provider.robotsOverride ? "Ignored (override attested)" : "Honored"}
            </Row>
            <Row label="Terms acknowledged">
                {formatDate(provider.termsAcknowledgedAt) || empty}
            </Row>
            <Row label="Recent crawls">
                {runs.length ? (
                    <ul className="provider-properties-runs" aria-label="Recent crawls">
                        {runs.slice(0, 5).map(run => (
                            <li key={run.id} className="provider-properties-runs__item">
                                <span>
                                    {`${formatDate(run.startedAt)}${runTriggerSuffix(run)}: ${runStatusLabel(run)}`}
                                    {run.error ? ` (${run.error})` : ""}
                                </span>
                                <Button
                                    variant="text"
                                    size="small"
                                    className="provider-properties-runs__csv"
                                    onClick={() => downloadCsv(run)}
                                    aria-label={`Download the inventory of the crawl started ${formatDate(run.startedAt)} as CSV`}
                                >
                                    CSV
                                </Button>
                            </li>
                        ))}
                        {runs.length > 5 && (
                            <li className="provider-properties-runs__more">
                                {`${runs.length - 5} older crawl(s) kept`}
                            </li>
                        )}
                    </ul>
                ) : (
                    empty
                )}
            </Row>
            <div className="provider-properties-action provider-properties-action--secondary">
                {pending && (
                    <Button
                        variant="text"
                        className="primary-button"
                        onClick={() => {
                            handleClose();
                            navigate(`/knowledge-base/${provider.id}/review/${pending.id}`);
                        }}
                    >
                        Review crawl
                    </Button>
                )}
                {user && (
                    <Button
                        variant="text"
                        className="thirt-button"
                        onClick={() => setEditOpen(true)}
                    >
                        Edit crawl settings
                    </Button>
                )}
            </div>
            {editOpen && user && (
                <CrawlSettingsDialog
                    open={editOpen}
                    companyId={user.companyId}
                    provider={provider}
                    handleClose={() => setEditOpen(false)}
                    handleSaved={() => {
                        setEditOpen(false);
                        loadProviders();
                        handleClose();
                    }}
                />
            )}
        </>
    );
};

export const ProviderPropertiesModal = ({
    open,
    provider,
    handleClose
}: {
    open: boolean;
    provider: Provider;
    handleClose: () => void;
}) => {
    const url = provider.providerUrl;
    const empty = "—";
    const isWebSite = provider.name === ProviderType.webScraper;

    return (
        <ModalTemplate open={open} width={654} handleClose={handleClose} ariaLabel="Properties">
            <div className="provider-properties-view">
                <div className="provider-properties-title">
                    <SvgIcon
                        className="provider-properties-title__icon"
                        component={SummaryIcon}
                        inheritViewBox
                    />
                    <span className="provider-properties-title__text">Properties</span>
                </div>
                <div className="provider-properties-list">
                    <Row label="Provider URL">
                        {url ? (
                            <a
                                className="provider-properties-row__link"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {url}
                            </a>
                        ) : (
                            empty
                        )}
                    </Row>
                    {!isWebSite && (
                        <Row label="Provider File ID">{provider.providerFileId ?? empty}</Row>
                    )}
                    <Row label="Provider Link Type">{provider.providerLinkType ?? empty}</Row>
                    <Row label="Type">{isWebSite ? "Web site" : (provider.name ?? empty)}</Row>
                    {!isWebSite && <Row label="Email">{provider.email ?? empty}</Row>}
                    <Row label="Status">
                        {isWebSite
                            ? (provider.syncStatus ?? "idle").replace(/_/g, " ")
                            : (provider.status ?? empty)}
                    </Row>
                    {isWebSite && (
                        <WebSiteProperties provider={provider} handleClose={handleClose} />
                    )}
                </div>
                <div className="provider-properties-action">
                    <Button variant="text" className="primary-button" onClick={handleClose}>
                        Close
                    </Button>
                </div>
            </div>
        </ModalTemplate>
    );
};
